import { useTranslation } from "react-i18next";
import { ConsumingOverlayPageProps, OverlayPage } from "../OverlayPage";
import {
  QuestionnaireResponse,
  useBookerContext,
  useCartContext,
} from "widgets/booker/contexts";
import { useMemo, useState } from "react";
import { Box } from "styled-system/jsx";
import { clearHiddenResponses } from "shared/components";
import { QuestionnaireQuestionList } from "widgets/booker/shared";
import _ from "lodash";
import { getMultilingualString } from "shared/utils";
import { getDefaultSurveyQuestionResponse } from "shared/components/organisms/SurveyQuestionInput/surveyQuestionCheckbox";
import { isSurveyDesignFieldType } from "shared/components/organisms/SurveyQuestionInput/surveyQuestionDesign";
import {
  areRequiredQuestionnaireResponsesAnswered,
  replaceOwnedQuestionnaireResponses,
} from "shared/components/organisms/SurveyQuestionInput/surveyQuestionnaire";

type QuestionnairePageProps = ConsumingOverlayPageProps;

export const QuestionnairePage = ({
  onClose,
  open,
  ...rest
}: QuestionnairePageProps) => {
  const {
    questionnaireResponses,
    completeQuestionnaire,
    updateQuestionnaireResponses,
    questionnaireComplete,
  } = useCartContext();
  const { booker, language } = useBookerContext();
  const { t } = useTranslation("booker", { keyPrefix: "QUESTIONNAIRE" });

  const defaultResponses = useMemo(() => {
    return (
      booker?.survey?.questions?.map(q => {
        const cartResponse = questionnaireResponses?.find(
          r => r.questionData._id === q._id,
        );
        return {
          questionData: q,
          response:
            cartResponse?.response ?? getDefaultSurveyQuestionResponse(q),
        };
      }) ?? []
    );
  }, [booker?.survey?.questions, questionnaireResponses]);

  const [responses, setResponses] =
    useState<QuestionnaireResponse[]>(defaultResponses);

  const handleResponseChange = (questionId: string, response: string[]) => {
    setResponses(prev =>
      clearHiddenResponses(
        prev.map(r =>
          r.questionData._id === questionId ? { ...r, response } : r,
        ),
      ),
    );
  };

  const isRequiredQuestionsAnswered = useMemo(
    () => areRequiredQuestionnaireResponsesAnswered(responses),
    [responses],
  );

  const handleClose = () => {
    onClose();
    setResponses(defaultResponses);
  };

  const handleDoneClick = () => {
    if (isRequiredQuestionsAnswered) {
      updateQuestionnaireResponses(current =>
        replaceOwnedQuestionnaireResponses(
          current,
          (booker?.survey?.questions ?? []).map(q => q._id),
          responses
            .filter(r => !isSurveyDesignFieldType(r.questionData.type))
            .map(r => ({ ...r, response: _.compact(r.response) })),
        ),
      );
      if (!questionnaireComplete) {
        completeQuestionnaire();
      }
      onClose();
    }
  };

  return (
    <OverlayPage
      title={
        getMultilingualString(booker?.survey?.name ?? {}, language) ||
        t("TITLE")
      }
      subtitle={t("DESCRIPTION")}
      open={open}
      onClose={handleClose}
      buttonDisabled={!isRequiredQuestionsAnswered}
      onButtonClick={handleDoneClick}
      {...rest}
    >
      <Box pt={"300"} pb="500">
        <QuestionnaireQuestionList
          questions={responses.map(r => r.questionData)}
          getResponse={q =>
            responses.find(r => r.questionData._id === q._id)?.response ?? []
          }
          onResponseChange={handleResponseChange}
        />
      </Box>
    </OverlayPage>
  );
};
